/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  MenuItem,
  OutlinedInput,
  Popover,
  PopoverProps,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  useGetConversionConfigQuery,
  useReportConvertionMutation,
} from '@src/reducers/capiAPI';
import React, { FormEvent, useEffect, useState } from 'react';
import {
  useController,
  useForm,
  UseFormReturn,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import currencyCodes from '../../utils/currencyCodes.json';
import Form, { FormSubmitEventHandler } from '../UI/Form';
import ButtonSubmit from '../UI/Form/ButtonSubmit';
import Field from '../UI/Form/Field';
import { useFieldControllerContext } from '../UI/Form/FieldControllerContext';
import SnackbarAlert from '../UI/SnackbarAlert';
import Typography from '../UI/Typography';
import { FieldNumberInput } from './NumberInput';

// eslint-disable-next-line @typescript-eslint/ban-types
export type ReportCAPIEventPopoverProps = Omit<PopoverProps, 'onSubmit'> & {
  chatId?: string;
  module: 'AUDIT' | 'TAGS' | 'CONSOLE';
  initialEvent?: 'LEAD' | 'PURCHASE';
  eventFieldReadOnly?: boolean;
  onSubmit?: (values: FormValues) => void;
  onSuccess: (values: FormValues) => void;
  onFailure?: (values: FormValues) => void;
};

export type FormValues = {
  event: 'LEAD' | 'PURCHASE';
  currency: string;
  amount: {
    value: string;
    valueAsNumber: number;
  };
};

export const initialValues = {
  event: '',
  currency: '',
  amount: {
    value: '0',
    valueAsNumber: 0,
  },
};
const ReportCAPIEventPopover: React.FC<ReportCAPIEventPopoverProps> = ({
  chatId,
  module,
  initialEvent,
  eventFieldReadOnly,
  onSubmit,
  onSuccess,
  onFailure,
  open,
  ...props
}) => {
  const form = useForm({
    defaultValues: initialValues,
  });

  const [reportEventTrigger, reportEventResult] = useReportConvertionMutation({
    fixedCacheKey: chatId,
  });

  const capiConfig = useGetConversionConfigQuery();
  const config = capiConfig.data?.manual;
  const enabled = !!config?.modules?.includes(module);

  useEffect(() => {
    if (open) form.setValue('event', initialEvent || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmitHandler: FormSubmitEventHandler<
    typeof initialValues
  > = values => {
    if (!chatId) return;
    if (reportEventResult.isLoading) return;

    reportEventTrigger({
      chatId,
      origin: module,
      type: values.event,
      ...(values.event === 'PURCHASE' && {
        purchase: {
          currency: values.currency,
          value: values.amount.valueAsNumber,
        },
      }),
    }).then(data => {
      if (!('error' in data)) {
        onSuccess(values as FormValues);
      } else {
        onFailure?.(values as FormValues);
      }
    });
    onSubmit?.(values as FormValues);
    props.onClose?.({}, 'escapeKeyDown');
  };

  return (
    <Popover {...props} open={enabled && open}>
      <ReportCAPIEventForm
        form={form}
        onSubmit={onSubmitHandler}
        disabled={reportEventResult.isLoading}
        loading={reportEventResult.isLoading}
        eventFieldReadOnly={eventFieldReadOnly}
      />
    </Popover>
  );
};
export default ReportCAPIEventPopover;

export type ReportCAPIEventFormProps = {
  form: UseFormReturn<typeof initialValues>;
  disabled?: boolean;
  onSubmit: FormSubmitEventHandler<typeof initialValues>;
  eventFieldReadOnly?: boolean;
  loading?: boolean;
};

export const ReportCAPIEventForm: React.FC<ReportCAPIEventFormProps> = ({
  form,
  disabled,
  onSubmit,
  eventFieldReadOnly,
  loading,
}) => {
  const eventType = useWatch({ control: form.control, name: 'event' });
  const { isValid } = useFormState({ control: form.control });

  return (
    <Box display="flex" flexDirection="column" gridGap={16} p={2}>
      <Typography fontWeight="bold">
        <FormattedMessage id="CAPI.ReportCAPIEvent" />
      </Typography>
      <Form
        form={form}
        display="flex"
        flexDirection="column"
        minWidth={300}
        disabled={disabled}
        onFormSubmit={onSubmit}
      >
        <Field.Controller name="event" rules={{ required: true }}>
          <Field.InputLabel>
            <FormattedMessage id="CAPI.EventType" />
          </Field.InputLabel>
          <Field.Select readOnly={eventFieldReadOnly}>
            <MenuItem value="LEAD">
              <FormattedMessage id="CAPI.Event.LEAD" />
            </MenuItem>
            <MenuItem value="PURCHASE">
              <FormattedMessage id="CAPI.Event.PURCHASE" />
            </MenuItem>
          </Field.Select>
        </Field.Controller>
        {eventType === 'PURCHASE' && (
          <>
            <Field.Controller
              name="currency"
              shouldUnregister
              rules={{ required: true }}
            >
              <Field.InputLabel>
                <FormattedMessage id="CAPI.SelectCurrency" />
              </Field.InputLabel>
              <FieldAutocompleteCurrency />
            </Field.Controller>
            <Field.Controller
              name="amount.value"
              shouldUnregister
              rules={{
                validate: (_, values) => values.amount.valueAsNumber > 0,
              }}
            >
              <Field.InputLabel>
                <FormattedMessage id="CAPI.EnterAmount" />
              </Field.InputLabel>
              <FieldNumberInput
                onNumberChange={value =>
                  form.setValue('amount.valueAsNumber', value)
                }
              />
            </Field.Controller>
          </>
        )}
        <Box display="flex" justifyContent="flex-end">
          <ButtonSubmit
            variant="contained"
            color="primary"
            loading={loading}
            disabled={!isValid}
          >
            <FormattedMessage id="Submit" />
          </ButtonSubmit>
        </Box>
      </Form>
    </Box>
  );
};

const FieldAutocompleteCurrency = () => {
  const { name, inputLabel } = useFieldControllerContext();
  const { field } = useController({ name });

  return (
    <Autocomplete
      value={field.value || ''}
      onChange={(_, value) => field.onChange(value)}
      options={currencyCodes}
      autoSelect
      selectOnFocus
      disableClearable
      autoHighlight
      getOptionLabel={code => code}
      renderInput={({ InputProps, inputProps, InputLabelProps, ...params }) => (
        <>
          <OutlinedInput
            {...params}
            {...InputProps}
            inputProps={inputProps}
            label={inputLabel}
            onInput={transformCurrencyOnInput}
          />
        </>
      )}
    />
  );
};

const transformCurrencyOnInput = (event: FormEvent<HTMLInputElement>) => {
  const target = event.target as HTMLInputElement;
  target.value = target.value.toUpperCase().replace(/[^A-Z]/g, '');
};

type CAPIEventReportedSnackbarProps = {
  onClose: () => void;
  event: string;
};
export const ReportedCAPIEventSnackbar: React.FC<
  CAPIEventReportedSnackbarProps
> = ({ event, onClose }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!!event);
  }, [event]);

  return (
    <SnackbarAlert
      key={event}
      open={open}
      onClose={() => setOpen(false)}
      TransitionProps={{
        onExited: () => {
          onClose();
        },
      }}
    >
      <FormattedMessage
        id="CAPI.ConversionReported"
        values={{
          event: <FormattedMessage id={`CAPI.Event.${event}`} />,
        }}
      />
    </SnackbarAlert>
  );
};
